import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, Headphones, ShieldCheck, Star, Play } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContentI18n } from "@/context/ContentI18n";
import { usePlayer } from "@/context/PlayerContext";
import { Header } from "@/components/Header";
import { PlayerBar } from "@/components/PlayerBar";
import { SponsorCard } from "@/components/SponsorCard";
import { fmtTime } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://images.unsplash.com/photo-1681301865120-7c74657dc01a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function PothKamare() {
  const { t, lang, fontClass } = useLang();
  const { title: ctitle, text: ctext } = useContentI18n();
  const { current } = usePlayer();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/books`).then((r) => { if (live) setBooks(r.data.filter((b) => b.category !== "kids")); })
      .catch(() => {}).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg)" }}>
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, color-mix(in srgb, var(--bg) 55%, transparent) 0%, color-mix(in srgb, var(--bg) 84%, transparent) 55%, var(--bg) 100%)" }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-12 sm:pt-20 sm:pb-16">
          <div className="max-w-2xl sk-fade-up">
            <span className="inline-flex items-center gap-2 font-ui text-xs sm:text-sm font-bold tracking-widest uppercase px-3 py-1.5 rounded-full mb-5" style={{ background: "var(--brass-light)", color: "var(--accent)" }}>
              <ShieldCheck size={16} /> {t("quiet_note")}
            </span>
            <h1 className={`${fontClass()} text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight`} style={{ color: "var(--text)" }}>
              {t("pk_title")}
            </h1>
            <p className="font-ui text-base sm:text-lg leading-relaxed mt-4" style={{ color: "var(--text-2)" }}>
              {t("pk_sub")}
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        {loading ? (
          <div className="py-16 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("loading")}</div>
        ) : books.length === 0 ? (
          <div className="py-16 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("pk_empty")}</div>
        ) : (
          <>
            {(() => {
              const feat = books.find((b) => b.is_featured);
              if (!feat) return null;
              return (
                <Link
                  to={`/rooms/poth-kamare/${feat.id}`}
                  data-testid={`book-featured-${feat.id}`}
                  className="block rounded-3xl p-5 sm:p-7 mb-6 transition-transform hover:scale-[1.01]"
                  style={{ background: "linear-gradient(120deg, #1C140E 0%, #3B2A16 60%, #92400E 140%)", color: "#FFFDF7" }}
                >
                  <div className="flex flex-col sm:flex-row items-center gap-5">
                    <img src={feat.cover_url} alt="" className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover shrink-0 shadow-lg" />
                    <div className="min-w-0 text-center sm:text-left">
                      <span className="inline-flex items-center gap-1.5 font-ui text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full" style={{ background: "rgba(245,158,11,0.18)", color: "var(--player-accent)" }}>
                        <Star size={13} fill="currentColor" /> {t("pk_featured")}
                      </span>
                      <h2 className={`${fontClass()} text-2xl sm:text-3xl font-bold mt-3 leading-tight`}>{ctitle(feat)}</h2>
                      <p className="font-ui text-sm mt-1" style={{ color: "rgba(255,255,255,0.7)" }}>{ctext(feat.author)} · {feat.chapter_count} {t("pk_chapters")}</p>
                      <span className="mt-4 inline-flex items-center gap-2 h-11 px-6 rounded-full font-ui text-sm font-bold" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}>
                        <Play size={18} fill="currentColor" /> {t("pk_listen")}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })()}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {books.map((b) => (
                <Link
                  key={b.id}
                  to={`/rooms/poth-kamare/${b.id}`}
                  data-testid={`book-card-${b.id}`}
                  className="text-left rounded-3xl p-4 transition-transform hover:scale-[1.02] active:scale-95"
                  style={{ background: "var(--card)", border: "2px solid var(--border)" }}
                >
                  <div className="relative">
                    <img src={b.cover_url} alt={b.title_en} className="w-full aspect-square rounded-2xl object-cover" />
                    {b.is_featured && (
                      <span className="absolute top-2 left-2 grid place-items-center w-8 h-8 rounded-full" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}><Star size={15} fill="currentColor" /></span>
                    )}
                    <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 font-ui text-xs font-bold px-2.5 py-1.5 rounded-full" style={{ background: "var(--player-bg)", color: "var(--player-accent)" }}>
                      <Headphones size={13} /> {b.chapter_count}
                    </span>
                  </div>
                  <p className={`${fontClass()} text-lg sm:text-xl font-bold mt-3 leading-snug line-clamp-2`} style={{ color: "var(--text)" }}>
                    {ctitle(b)}
                  </p>
                  <p className="font-ui text-sm truncate mt-0.5" style={{ color: "var(--text-muted)" }}>
                    {ctext(b.author)}
                  </p>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      <SponsorCard />

      {current && <div style={{ height: 8 }} />}
      <PlayerBar />
    </div>
  );
}
